import { AnimatePresence, motion } from 'framer-motion'
import { rgba } from 'polished'
import React, { useRef } from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled, { css } from 'styled-components'
import {
  COLOR_INTENT,
  MODAL_MAX_WIDTH,
  SLIDE_OUT_CUBIC_BEZIER_EASE,
  Z_INDEX,
} from 'Theme'
import { useMedia } from 'useMedia'
import { type ModalProps, Overlay } from './Modal'
import { ModalContent } from './ModalContent'

const ANIMATION_VARIANT = {
  OPEN: 'OPEN',
  CLOSE: 'CLOSE',
}

const overlayVariants = {
  [ANIMATION_VARIANT.OPEN]: {
    backgroundColor: rgba(COLOR_INTENT.GRAY_90, 0.3),
  },
  [ANIMATION_VARIANT.CLOSE]: {
    backgroundColor: rgba(COLOR_INTENT.GRAY_90, 0),
  },
}

const slideUpDialogVariants = {
  [ANIMATION_VARIANT.OPEN]: {
    y: '0',
    transition: SLIDE_OUT_CUBIC_BEZIER_EASE,
  },
  [ANIMATION_VARIANT.CLOSE]: {
    y: '100%',
    transition: SLIDE_OUT_CUBIC_BEZIER_EASE,
  },
}

const MotionOverlay = styled(motion.div)`
  display: grid;
  position: fixed;
  overflow: hidden;
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0;
  align-content: end;
  justify-items: center;
  z-index: ${Z_INDEX.SLIDE_OUT_OVERLAY};
`

const MotionContent = styled(motion.div)`
  width: 100%;
  ${({ isAttachedToBottom }) =>
    isAttachedToBottom &&
    css`
      height: 100vh;
      max-height: -webkit-fill-available;
    `}
`

const SlideOutModal: React.FC<React.PWC<ModalProps>> = ({
  children,
  isOpen,
  onModalClose,
  isAttachedToBottom,
  ...rest
}) => {
  const media = useMedia()
  const contentRef = useRef(null)

  if (media.MOBILE) {
    return (
      <AnimatePresence mode="wait">
        {isOpen && (
          <MotionOverlay
            key="overlay"
            variants={overlayVariants}
            initial={ANIMATION_VARIANT.CLOSE}
            animate={ANIMATION_VARIANT.OPEN}
            exit={ANIMATION_VARIANT.CLOSE}
            onClick={onModalClose}
            initialFocusRef={contentRef}
            data-test-id={DATA_TEST_ID.MODAL_OVERLAY}
          >
            <MotionContent
              variants={slideUpDialogVariants}
              isAttachedToBottom={isAttachedToBottom}
            >
              <ModalContent
                onModalClose={onModalClose}
                onClick={(event) => event.stopPropagation()}
                isMobile
                isOpen
                isWithHeaderDelimiter={false}
                isWithFooterDelimiter={false}
                isAttachedToBottom={isAttachedToBottom}
                {...rest}
                data-test-id={DATA_TEST_ID.MODAL_CONTENT}
              >
                {children}
              </ModalContent>
            </MotionContent>
          </MotionOverlay>
        )}
      </AnimatePresence>
    )
  }
  return (
    <Overlay
      isOpen={isOpen}
      onDismiss={onModalClose}
      initialFocusRef={contentRef}
      data-test-id={DATA_TEST_ID.MODAL_OVERLAY}
    >
      <ModalContent
        maxWidth={MODAL_MAX_WIDTH.PX_720}
        isOpen={isOpen}
        onModalClose={onModalClose}
        isWithHeaderDelimiter={false}
        isWithFooterDelimiter={false}
        {...rest}
      >
        {children}
      </ModalContent>
    </Overlay>
  )
}

export default SlideOutModal
